import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FiBell, FiX, FiCheck, FiCheckCircle, FiVolume2, FiVolumeX,
    FiEye, FiTrash2, FiExternalLink, FiCalendar, FiMessageSquare,
    FiAlertTriangle, FiFileText, FiCheckSquare, FiChevronDown, FiChevronUp
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import {
    getNotifications, markNotificationAsRead, markAllNotificationsAsRead,
    deleteNotification, subscribeToNotifications
} from '../../lib/supabase/notifications';
import notificationSound from '../../lib/soundUtils/notificationSound';

const NotificationMenu = ({ isOpen, onClose, onRefreshBadge, isSidebarOpen }) => {
    const { userProfile, company } = useAuth();
    const navigate = useNavigate();

    const [notifications, setNotifications] = useState([]);
    const [showingOld, setShowingOld] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [volume, setVolume] = useState(0.7);
    const [expandedNotificationId, setExpandedNotificationId] = useState(null);
    const menuRef = useRef(null);

    useEffect(() => {
        const savedVolume = localStorage.getItem('notificationVolume');
        const savedMuted = localStorage.getItem('notificationMuted');
        if (savedVolume) setVolume(parseFloat(savedVolume));
        if (savedMuted) setIsMuted(savedMuted === 'true');
    }, []);

    useEffect(() => {
        if (isOpen && userProfile) {
            fetchNotifications();
            // Refresh badge when opening menu
            if (onRefreshBadge) {
                onRefreshBadge();
            }
        }
    }, [isOpen, userProfile, showingOld, onRefreshBadge]);

    useEffect(() => {
        if (!userProfile?.id) return;

        const subscription = subscribeToNotifications(userProfile.id, (notification) => {
            if (!isMuted) {
                notificationSound.play(volume);
            }
            fetchNotifications();
        });

        return () => {
            if (subscription) subscription.unsubscribe();
        };
    }, [userProfile, isMuted, volume]);

    const fetchNotifications = async () => {
        try {
            if (!userProfile?.id) return;
            const data = await getNotifications(userProfile.id, showingOld);
            setNotifications(data);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        }
    };

    const handleMarkAsRead = async (notificationId) => {
        try {
            await markNotificationAsRead(notificationId);
            fetchNotifications();
            if (onRefreshBadge) {
                onRefreshBadge();
            }
        } catch (error) {
            console.error('Error marking as read:', error);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            await markAllNotificationsAsRead(userProfile.id);
            fetchNotifications();
            if (onRefreshBadge) {
                onRefreshBadge();
            }
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };

    const handleDelete = async (notificationId) => {
        try {
            await deleteNotification(notificationId);
            fetchNotifications();
            if (onRefreshBadge) {
                onRefreshBadge();
            }
        } catch (error) {
            console.error('Error deleting notification:', error);
        }
    };

    const handleVolumeChange = (newVolume) => {
        setVolume(newVolume);
        localStorage.setItem('notificationVolume', newVolume.toString());
    };

    const handleMuteToggle = () => {
        const newMuted = !isMuted;
        setIsMuted(newMuted);
        localStorage.setItem('notificationMuted', newMuted.toString());
    };

    const getNotificationIcon = (type) => {
        const iconProps = { className: "w-4 h-4" };
        switch (type) {
            case 'assignment_created':
            case 'assignment_updated':
                return <FiFileText {...iconProps} className="w-4 h-4 text-blue-600" />;
            case 'assignment_completed':
                return <FiCheckSquare {...iconProps} className="w-4 h-4 text-green-600" />;
            case 'assignment_due_soon':
                return <FiCalendar {...iconProps} className="w-4 h-4 text-orange-600" />;
            case 'message_received':
                return <FiMessageSquare {...iconProps} className="w-4 h-4 text-purple-600" />;
            case 'system_alert':
                return <FiAlertTriangle {...iconProps} className="w-4 h-4 text-red-600" />;
            default:
                return <FiBell {...iconProps} className="w-4 h-4 text-blue-600" />;
        }
    };

    const getPriorityColors = (priority) => {
        switch (priority) {
            case 'urgent':
                return { border: 'border-l-red-500', bg: 'bg-red-50 dark:bg-red-900/10', badge: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' };
            case 'high':
                return { border: 'border-l-orange-500', bg: 'bg-orange-50 dark:bg-orange-900/10', badge: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' };
            case 'normal':
                return { border: 'border-l-blue-500', bg: 'bg-blue-50 dark:bg-blue-900/10', badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' };
            case 'low':
                return { border: 'border-l-gray-400', bg: 'bg-gray-50 dark:bg-gray-900/10', badge: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300' };
            default:
                return { border: 'border-l-blue-500', bg: 'bg-blue-50 dark:bg-blue-900/10', badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' };
        }
    };

    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffInMinutes = Math.floor((now - date) / (1000 * 60));
        if (diffInMinutes < 1) return 'Just now';
        if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
        const diffInHours = Math.floor(diffInMinutes / 60);
        if (diffInHours < 24) return `${diffInHours}h ago`;
        const diffInDays = Math.floor(diffInHours / 24);
        if (diffInDays < 7) return `${diffInDays}d ago`;
        return date.toLocaleDateString();
    };

    const handleNotificationClick = (notification) => {
        // Toggle expansion - if clicking the same notification, collapse it; otherwise expand the new one
        if (expandedNotificationId === notification.id) {
            setExpandedNotificationId(null);
        } else {
            setExpandedNotificationId(notification.id);
        }

        // Navigate if there's an action URL
        if (notification.action_url) {
            navigate(notification.action_url);
            if (!notification.is_read) {
                handleMarkAsRead(notification.id);
            }
            onClose();
        }
    };

    const unreadCount = notifications.filter(n => !n.is_read).length;

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />
            <div
                ref={menuRef}
                className={`fixed bottom-4 ${isSidebarOpen ? 'left-[17rem]' : 'left-24'} w-[440px] h-[600px] bg-white dark:bg-zinc-800 rounded-xl shadow-2xl border border-gray-200 dark:border-zinc-700 z-50 overflow-hidden flex flex-col transition-all duration-300`}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-zinc-700 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 dark:from-blue-900/20 dark:to-indigo-900/20 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                            <FiBell className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-900 dark:text-white">Notifications</h3>
                            {unreadCount > 0 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                                    {unreadCount} unread
                                </span>
                            )}
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white/70 dark:hover:bg-zinc-700 rounded-xl transition-colors">
                        <FiX className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    </button>
                </div>

                {/* Controls */}
                <div className="flex items-center justify-between p-4 bg-white/70 dark:bg-zinc-900/50 border-b border-gray-200 dark:border-zinc-700 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleMuteToggle}
                            className={`p-2 rounded-lg transition-all ${isMuted
                                ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                                : 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                                }`}
                        >
                            {isMuted ? <FiVolumeX className="w-4 h-4" /> : <FiVolume2 className="w-4 h-4" />}
                        </button>
                        {!isMuted && (
                            <div className="flex items-center gap-2">
                                <input
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.1"
                                    value={volume}
                                    onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                                    className="w-20 h-2 bg-gray-200 dark:bg-zinc-700 rounded-lg appearance-none cursor-pointer"
                                />
                                <button
                                    onClick={() => notificationSound.testSound()}
                                    className="px-3 py-1.5 text-xs font-medium bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50 rounded-lg transition-colors"
                                >
                                    Test
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setShowingOld(!showingOld)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-zinc-700 rounded-lg transition-colors"
                        >
                            <FiEye className="w-3 h-3" />
                            {showingOld ? 'Recent' : 'Show Read'}
                        </button>
                        {unreadCount > 0 && !showingOld && (
                            <button
                                onClick={handleMarkAllAsRead}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                            >
                                <FiCheckCircle className="w-3 h-3" />
                                Mark All Read
                            </button>
                        )}
                    </div>
                </div>

                {/* Notifications List */}
                <div className="flex-1 overflow-y-auto">
                    {notifications.length === 0 ? (
                        <div className="p-12 text-center text-gray-500 dark:text-gray-400">
                            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-zinc-700 rounded-2xl flex items-center justify-center">
                                <FiBell className="w-6 h-6 text-gray-400" />
                            </div>
                            <p className="text-sm font-medium mb-2">No notifications</p>
                            <p className="text-xs text-gray-400 leading-relaxed">
                                {showingOld ? 'No read notifications found' : "You're all caught up! 🎉"}
                            </p>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-100 dark:divide-zinc-700">
                            {notifications.map((notification) => {
                                const priorityColors = getPriorityColors(notification.priority);
                                const isExpanded = expandedNotificationId === notification.id;
                                return (
                                    <div
                                        key={notification.id}
                                        className={`group p-4 hover:bg-gray-50/70 dark:hover:bg-zinc-700/50 transition-all relative cursor-pointer ${!notification.is_read ? `border-l-3 ${priorityColors.border} ${priorityColors.bg}` : ''
                                            }`}
                                        onClick={() => handleNotificationClick(notification)}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className="flex-shrink-0 mt-1">
                                                {getNotificationIcon(notification.type)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between gap-3">
                                                    <div className="flex-1">
                                                        <h4 className={`text-sm font-semibold ${!notification.is_read ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'
                                                            } leading-tight mb-1`}>
                                                            {notification.title}
                                                        </h4>
                                                        {notification.message && (
                                                            <div className="relative">
                                                                <p className={`text-xs text-gray-600 dark:text-gray-400 leading-relaxed mb-3 ${
                                                                    isExpanded ? '' : 'line-clamp-2'
                                                                }`}>
                                                                    {notification.message}
                                                                </p>
                                                                {/* Show expand/collapse indicator if message is long */}
                                                                {notification.message.length > 100 && (
                                                                    <div className="flex items-center gap-1 text-xs text-blue-500 dark:text-blue-400 font-medium mb-2">
                                                                        {isExpanded ? (
                                                                            <>
                                                                                <FiChevronUp className="w-3 h-3" />
                                                                                <span>Show less</span>
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <FiChevronDown className="w-3 h-3" />
                                                                                <span>Show more</span>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-xs text-gray-400 font-medium">
                                                                    {formatTimestamp(notification.created_at)}
                                                                </span>
                                                                {(notification.priority === 'urgent' || notification.priority === 'high') && (
                                                                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${priorityColors.badge}`}>
                                                                        {notification.priority === 'urgent' ? 'Urgent' : 'High'}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        {!notification.is_read && (
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleMarkAsRead(notification.id);
                                                                }}
                                                                className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors"
                                                                title="Mark as read"
                                                            >
                                                                <FiCheck className="w-3 h-3" />
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDelete(notification.id);
                                                            }}
                                                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                                                            title="Delete notification"
                                                        >
                                                            <FiTrash2 className="w-3 h-3" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {!notification.is_read && (
                                            <div className="absolute right-3 top-4 w-2 h-2 bg-blue-500 rounded-full shadow-sm"></div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                {notifications.length > 0 && (
                    <div className="p-3 bg-gray-50/70 dark:bg-zinc-900/50 border-t border-gray-200 dark:border-zinc-700 flex-shrink-0">
                        <div className="flex items-center justify-center">
                            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                                {notifications.length} notification{notifications.length !== 1 ? 's' : ''} displayed
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default NotificationMenu;
